import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, Bot, Settings, ArrowLeft, RefreshCw, Plus, 
  Edit, Trash2, Eye, EyeOff, ChevronDown, ChevronRight, Search,
  UserPlus, Mail, Shield, Check, X, MoreHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import api from '@/utils/api';
import { toast } from 'sonner';

const TORTLAB_LOGO = "https://customer-assets.emergentagent.com/job_35f2eb62-f2f6-4b48-9388-e2c1ee261a61/artifacts/tfwz2zen_generated-image-1%20%284%29.png";

export default function AdminPanel() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTenants, setExpandedTenants] = useState({});
  
  // Dialog states
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [showEditTenant, setShowEditTenant] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Form states
  const [tenantForm, setTenantForm] = useState({ name: '', primary_color: '#6366f1' });
  const [userForm, setUserForm] = useState({ 
    email: '', password: '', name: '', role: 'ClientAdmin', tenant_id: '' 
  });
  const [agentForm, setAgentForm] = useState({
    name: '', context_text: '', tenant_id: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [tenantsRes, usersRes, agentsRes] = await Promise.all([
        api.get('/admin/tenants'),
        api.get('/admin/users'),
        api.get('/admin/all-agents')
      ]);
      setTenants(tenantsRes.data.tenants || []);
      setAllUsers(usersRes.data.users || []);
      setAllAgents(agentsRes.data.agents || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const toggleTenantExpand = (tenantId) => {
    setExpandedTenants(prev => ({
      ...prev,
      [tenantId]: !prev[tenantId]
    }));
  };

  const getTenantUsers = (tenantId) => {
    return allUsers.filter(u => u.tenant_id === tenantId);
  };

  const getTenantAgents = (tenantId) => {
    return allAgents.filter(a => a.tenant_id === tenantId);
  };

  const handleCreateTenant = async () => {
    if (!tenantForm.name.trim()) {
      toast.error('Tenant name is required');
      return;
    }
    setProcessing(true);
    try {
      await api.post('/admin/tenants', tenantForm);
      toast.success('Tenant created successfully');
      setShowCreateTenant(false);
      setTenantForm({ name: '', primary_color: '#6366f1' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateTenant = async () => {
    if (!selectedTenant) return;
    setProcessing(true);
    try {
      await api.put(`/admin/tenants/${selectedTenant.tenant_id}`, tenantForm);
      toast.success('Tenant updated successfully');
      setShowEditTenant(false);
      setSelectedTenant(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleTenantActive = async (tenant) => {
    try {
      await api.put(`/admin/tenants/${tenant.tenant_id}`, {
        is_active: !tenant.is_active
      });
      toast.success(`Tenant ${tenant.is_active ? 'deactivated' : 'activated'}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update tenant status');
    }
  };

  const handleCreateUser = async () => {
    // Validate based on role
    if (!userForm.email || !userForm.password || !userForm.name) {
      toast.error('Name, email, and password are required');
      return;
    }
    
    // Tenant is required for non-PlatformAdmin roles
    if (userForm.role !== 'PlatformAdmin' && !userForm.tenant_id) {
      toast.error('Please select a tenant for this user');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post('/admin/users', userForm);
      toast.success(userForm.role === 'PlatformAdmin' 
        ? 'Platform Admin created successfully!' 
        : 'User created successfully');
      setShowCreateUser(false);
      setUserForm({ email: '', password: '', name: '', role: 'ClientAdmin', tenant_id: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!agentForm.name || !agentForm.tenant_id) {
      toast.error('Agent name and tenant are required');
      return;
    }
    setProcessing(true);
    try {
      await api.post('/admin/agents', agentForm);
      toast.success('Agent created successfully');
      setShowCreateAgent(false);
      setAgentForm({ name: '', context_text: '', tenant_id: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!confirm('Are you sure you want to delete this agent? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/agents/${agentId}`);
      toast.success('Agent deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleAgentActive = async (agent) => {
    try {
      await api.put(`/agents/${agent.agent_id}`, {
        is_active: !agent.is_active
      });
      toast.success(`Agent ${agent.is_active ? 'deactivated' : 'activated'}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.tenant_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setTenantForm({ 
      name: tenant.name, 
      primary_color: tenant.primary_color || '#6366f1' 
    });
    setShowEditTenant(true);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'PlatformAdmin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'ClientAdmin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'AgentAdmin': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img src={TORTLAB_LOGO} alt="TortLab" className="h-8 object-contain" />
              <div>
                <h1 className="text-xl font-heading font-bold text-foreground">Admin Panel</h1>
                <p className="text-sm text-muted-foreground">Manage Tenants, Users & Agents</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={fetchAllData} className="rounded-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setUserForm({ email: '', password: '', name: '', role: 'PlatformAdmin', tenant_id: '' });
                  setShowCreateUser(true);
                }} 
                className="rounded-full"
              >
                <Shield className="w-4 h-4 mr-2" />
                Add Admin
              </Button>
              <Button size="sm" onClick={() => setShowCreateTenant(true)} className="rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                New Tenant
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Tenants</p>
                  <p className="text-3xl font-bold">{tenants.length}</p>
                </div>
                <Building2 className="w-8 h-8 text-primary/20" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-3xl font-bold">{allUsers.length}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500/20" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Agents</p>
                  <p className="text-3xl font-bold">{allAgents.length}</p>
                </div>
                <Bot className="w-8 h-8 text-green-500/20" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                  <p className="text-3xl font-bold">
                    {tenants.filter(t => t.subscription_status === 'active').length}
                  </p>
                </div>
                <Check className="w-8 h-8 text-emerald-500/20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-tenants"
            />
          </div>
        </div>

        {/* Tenants List */}
        <div className="space-y-4">
          {filteredTenants.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-12 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No tenants found</p>
              </CardContent>
            </Card>
          ) : (
            filteredTenants.map((tenant) => {
              const tenantUsers = getTenantUsers(tenant.tenant_id);
              const tenantAgents = getTenantAgents(tenant.tenant_id);
              const isExpanded = expandedTenants[tenant.tenant_id];

              return (
                <Card key={tenant.tenant_id} className="border-2" data-testid={`tenant-${tenant.tenant_id}`}>
                  <Collapsible open={isExpanded} onOpenChange={() => toggleTenantExpand(tenant.tenant_id)}>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <CollapsibleTrigger asChild>
                          <div className="flex items-center space-x-4 cursor-pointer flex-1">
                            <div className="flex items-center">
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-muted-foreground" />
                              )}
                            </div>
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: tenant.primary_color || '#6366f1' }}
                            >
                              {tenant.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h3 className="font-semibold">{tenant.name}</h3>
                                <Badge variant={tenant.is_active ? 'default' : 'secondary'} className="text-xs">
                                  {tenant.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                                {tenant.subscription_status && (
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {tenant.subscription_status}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {tenantUsers.length} users • {tenantAgents.length} agents
                              </p>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUserForm({ ...userForm, tenant_id: tenant.tenant_id });
                              setShowCreateUser(true);
                            }}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Add User
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAgentForm({ ...agentForm, tenant_id: tenant.tenant_id });
                              setShowCreateAgent(true);
                            }}
                          >
                            <Bot className="w-4 h-4 mr-2" />
                            Add Agent
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditTenant(tenant)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Tenant
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleTenantActive(tenant)}>
                                {tenant.is_active ? (
                                  <>
                                    <EyeOff className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <Eye className="w-4 h-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>

                    <CollapsibleContent>
                      <div className="border-t px-4 py-4 bg-slate-50/50">
                        <Tabs defaultValue="users" className="w-full">
                          <TabsList className="mb-4">
                            <TabsTrigger value="users">
                              <Users className="w-4 h-4 mr-2" />
                              Users ({tenantUsers.length})
                            </TabsTrigger>
                            <TabsTrigger value="agents">
                              <Bot className="w-4 h-4 mr-2" />
                              Agents ({tenantAgents.length})
                            </TabsTrigger>
                            <TabsTrigger value="details">
                              <Settings className="w-4 h-4 mr-2" />
                              Details
                            </TabsTrigger>
                          </TabsList>

                          <TabsContent value="users">
                            {tenantUsers.length === 0 ? (
                              <p className="text-sm text-muted-foreground py-4">No users in this tenant</p>
                            ) : (
                              <div className="space-y-2">
                                {tenantUsers.map((user) => (
                                  <div 
                                    key={user.user_id} 
                                    className="flex items-center justify-between p-3 bg-white rounded-lg border"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                        <Mail className="w-4 h-4 text-slate-500" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{user.name || user.email}</p>
                                        <p className="text-xs text-muted-foreground">{user.email}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <Badge className={`text-xs border ${getRoleBadgeColor(user.role)}`}>
                                        {user.role}
                                      </Badge>
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => handleDeleteUser(user.user_id)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </TabsContent>

                          <TabsContent value="agents">
                            {tenantAgents.length === 0 ? (
                              <p className="text-sm text-muted-foreground py-4">No agents in this tenant</p>
                            ) : (
                              <div className="space-y-2">
                                {tenantAgents.map((agent) => (
                                  <div 
                                    key={agent.agent_id} 
                                    className="flex items-center justify-between p-3 bg-white rounded-lg border"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Bot className="w-4 h-4 text-primary" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{agent.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                          Token: {agent.public_agent_token?.slice(0, 8)}...
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <Badge variant={agent.is_active ? 'default' : 'secondary'} className="text-xs">
                                        {agent.is_active ? 'Active' : 'Inactive'}
                                      </Badge>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigate(`/agents/${agent.agent_id}/settings`)}
                                      >
                                        <Settings className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleToggleAgentActive(agent)}
                                      >
                                        {agent.is_active ? (
                                          <EyeOff className="w-4 h-4 text-muted-foreground" />
                                        ) : (
                                          <Eye className="w-4 h-4 text-muted-foreground" />
                                        )}
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => handleDeleteAgent(agent.agent_id)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </TabsContent>

                          <TabsContent value="details">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">Tenant ID</p>
                                <p className="font-mono">{tenant.tenant_id}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Created</p>
                                <p>{new Date(tenant.created_at).toLocaleDateString()}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Subscription Status</p>
                                <p className="capitalize">{tenant.subscription_status || 'None'}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Plan</p>
                                <p>{tenant.pricing_plan?.name || 'No plan'}</p>
                              </div>
                              {tenant.hardware_status && (
                                <div>
                                  <p className="text-muted-foreground">Hardware Status</p>
                                  <p className="capitalize">{tenant.hardware_status.replace('_', ' ')}</p>
                                </div>
                              )}
                              {tenant.hardware_serial && (
                                <div>
                                  <p className="text-muted-foreground">Hardware Serial</p>
                                  <p className="font-mono">{tenant.hardware_serial}</p>
                                </div>
                              )}
                            </div>
                          </TabsContent>
                        </Tabs>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })
          )}
        </div>
      </main>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreateTenant} onOpenChange={setShowCreateTenant}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
            <DialogDescription>Add a new organization to the platform</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Organization Name</Label>
              <Input
                value={tenantForm.name}
                onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                placeholder="Acme Corporation"
              />
            </div>
            <div className="space-y-2">
              <Label>Brand Color</Label>
              <div className="flex items-center space-x-2">
                <Input
                  type="color"
                  value={tenantForm.primary_color}
                  onChange={(e) => setTenantForm({ ...tenantForm, primary_color: e.target.value })}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={tenantForm.primary_color}
                  onChange={(e) => setTenantForm({ ...tenantForm, primary_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTenant(false)}>Cancel</Button>
            <Button onClick={handleCreateTenant} disabled={processing}>
              {processing ? 'Creating...' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={showEditTenant} onOpenChange={setShowEditTenant}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
            <DialogDescription>Update organization details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Organization Name</Label>
              <Input
                value={tenantForm.name}
                onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Brand Color</Label>
              <div className="flex items-center space-x-2">
                <Input
                  type="color"
                  value={tenantForm.primary_color}
                  onChange={(e) => setTenantForm({ ...tenantForm, primary_color: e.target.value })}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={tenantForm.primary_color}
                  onChange={(e) => setTenantForm({ ...tenantForm, primary_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTenant(false)}>Cancel</Button>
            <Button onClick={handleUpdateTenant} disabled={processing}>
              {processing ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              {userForm.role === 'PlatformAdmin' 
                ? 'Add a new Platform Administrator' 
                : 'Add a user to the selected tenant'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select 
                value={userForm.role} 
                onValueChange={(value) => setUserForm({ 
                  ...userForm, 
                  role: value,
                  tenant_id: value === 'PlatformAdmin' ? '' : userForm.tenant_id 
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PlatformAdmin">
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-purple-500" />
                      Platform Admin
                    </div>
                  </SelectItem>
                  <SelectItem value="ClientAdmin">Client Admin</SelectItem>
                  <SelectItem value="AgentAdmin">Agent Admin</SelectItem>
                </SelectContent>
              </Select>
              {userForm.role === 'PlatformAdmin' && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Platform Admins have full access to all tenants and system settings
                </p>
              )}
            </div>
            
            {userForm.role !== 'PlatformAdmin' && (
              <div className="space-y-2">
                <Label>Tenant</Label>
                <Select 
                  value={userForm.tenant_id} 
                  onValueChange={(value) => setUserForm({ ...userForm, tenant_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={processing}>
              {processing ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Agent Dialog */}
      <Dialog open={showCreateAgent} onOpenChange={setShowCreateAgent}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Agent</DialogTitle>
            <DialogDescription>Add a chat agent to the selected tenant</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tenant</Label>
              <Select 
                value={agentForm.tenant_id} 
                onValueChange={(value) => setAgentForm({ ...agentForm, tenant_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Agent Name</Label>
              <Input
                value={agentForm.name}
                onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                placeholder="Customer Support Bot"
              />
            </div>
            <div className="space-y-2">
              <Label>Context / Instructions (Optional)</Label>
              <textarea
                value={agentForm.context_text}
                onChange={(e) => setAgentForm({ ...agentForm, context_text: e.target.value })}
                placeholder="You are a helpful customer support agent..."
                className="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAgent(false)}>Cancel</Button>
            <Button onClick={handleCreateAgent} disabled={processing}>
              {processing ? 'Creating...' : 'Create Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
